import { useTranslation } from 'react-i18next';

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';

import {
  detailColumnSpecs,
  HOURS_GROUPS,
  HOURS_SECTIONS
} from '../../columns/columnSpec';
import { resolveHeaderLabel } from '../../columns/resolveHeaderLabel';
import { flipsSign } from '../../columns/useEmploymentHoursColumns';
import { useHoursDetailLayout } from '../../hooks/useHoursDetailLayout';
import { correctionKeyForSource } from '../HoursCorrectionDialog/correctionSpec';
import {
  DetailGrid,
  DetailPanel,
  DetailPanelHeader,
  DetailSectionBlock,
  DetailSectionHeading
} from '../../EmploymentHours.styles';
import { formatHours } from '../../EmploymentHours.utils';
import {
  SIGN_FLIPPED_KEYS,
  toDisplayValue
} from '../HoursCorrectionDialog/hoursCorrection.utils';
import DetailTablePicker from './DetailTablePicker';
import SortableDetailTable from './SortableDetailTable';

import type { DragEndEvent } from '@dnd-kit/core';
import type {
  HoursColumnSpec,
  HoursGroupId
} from '../../columns/columnSpec.types';
import type { CorrectionEditableKey } from '../HoursCorrectionDialog/correctionSpec.types';
import type { HoursRowDetailPanelProps } from './HoursRowDetailPanel.types';

/**
 * Vertical breakdown of one month, replacing the horizontal scroll through ~66 detail columns.
 *
 * Built from the same `HOURS_COLUMN_SPECS` table as the grid, so a column added to the spec appears
 * here automatically and the two can never disagree. Shows the groups that are hidden by default —
 * exactly the ones Dojo put behind click-to-expand header links.
 *
 * Grouped like the grid header: one stacked block per section, its group tables laid out left to
 * right inside it. Each table can be dragged within its section or removed, persisted per user.
 *
 * A value that takes a correction gets a second cell holding it — an input when the enclosing
 * `HoursDetailDialog` form allows editing, plain text otherwise.
 */
const HoursRowDetailPanel = ({
  row,
  flags,
  plbName,
  selectedYear,
  editableKeys = [],
  canEdit = false,
  showPlusMinus = false,
  showBalanceBudget = false
}: HoursRowDetailPanelProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const labelContext = { t, plbName, selectedYear };
  const claFlags = flags ?? [];

  const { orderedGroups, isRemoved, moveGroup, toggleGroup } =
    useHoursDetailLayout();

  // dragging must not start on a plain click inside the table, hence the small distance threshold
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const detailSpecs = (groupId: HoursGroupId) =>
    detailColumnSpecs(claFlags, showBalanceBudget).filter(
      (spec) => spec.group === groupId
    );

  // every table the flags produce, in the user's order; removed ones stay in for the picker
  const sections = HOURS_SECTIONS.map((section) => ({
    section,
    groups: orderedGroups(section.groups).flatMap((groupId) => {
      const group = HOURS_GROUPS.find((candidate) => candidate.id === groupId);
      const specs = detailSpecs(groupId);

      return group && specs.length > 0 ? [{ group, specs }] : [];
    })
  })).filter(({ groups }) => groups.length > 0);

  const shownSections = sections
    .map((entry) => ({
      ...entry,
      groups: entry.groups.filter(({ group }) => !isRemoved(group.id))
    }))
    .filter(({ groups }) => groups.length > 0);

  const onDragEnd =
    (sectionGroupIds: HoursGroupId[]) => (event: DragEndEvent) => {
      if (event.over && event.active.id !== event.over.id) {
        moveGroup(
          sectionGroupIds,
          event.active.id as HoursGroupId,
          event.over.id as HoursGroupId
        );
      }
    };

  const labels = {
    reorder: t('employmentHours.detail.reorderTable'),
    remove: t('employmentHours.detail.removeTable'),
    value: t('employmentHours.detail.value'),
    correction: t('employmentHours.detail.correction')
  };

  /**
   * The grid flips these columns' sign under the plus/minus preference; so must the window. Reuses
   * the grid's own predicate so the balance columns (flipped by `render`, not by
   * `signFlipWhenPlusMinus`) agree between the two views.
   */
  const displayValue = (spec: HoursColumnSpec) => {
    const stored = row[spec.source] as number | undefined;
    const flip = showPlusMinus && flipsSign(spec);

    return formatHours(toDisplayValue(stored, flip));
  };

  /**
   * The correction beside the value it corrects; the pairing is the correction form's own spec.
   * `editableKeys` is the whitelist the CLA flags expose, so a gated correction never appears.
   */
  const detailRow = (spec: HoursColumnSpec) => {
    const key = correctionKeyForSource(spec.source);
    const isEditable = Boolean(key) && editableKeys.includes(key as never);

    return {
      id: spec.id,
      label: resolveHeaderLabel(spec.label, labelContext),
      // the running total is computed from the correction being typed, so it has no stored value
      ...(spec.render === 'totalHours'
        ? { totalOf: spec.source }
        : {
            // a correction that IS the column corrects itself: one number, in the correction cell
            value: isEditable && key === spec.source ? '' : displayValue(spec)
          }),
      ...(isEditable &&
        (canEdit
          ? { correctionName: key }
          : {
              // the editable input shows the display-space (flipped) value, so the read-only
              // rendering of the same field must apply the same flip or the two disagree
              correction: formatHours(
                toDisplayValue(
                  row[key as never] as number,
                  showPlusMinus &&
                    SIGN_FLIPPED_KEYS.includes(key as CorrectionEditableKey)
                )
              )
            }))
    };
  };

  return (
    <DetailPanel>
      <DetailPanelHeader>
        {/* always rendered, not just when a table is removed: the picker owns removal too, and
            keeping it mounted while it is open means re-adding the last table can't close it
            out from under the user */}
        <DetailTablePicker
          sections={sections.map(({ section, groups }) => ({
            id: section.id,
            title: resolveHeaderLabel(section.label, labelContext),
            tables: groups.map(({ group }) => ({
              group: group.id,
              title: resolveHeaderLabel(group.label, labelContext)
            }))
          }))}
          isRemoved={isRemoved}
          onToggle={toggleGroup}
        />
      </DetailPanelHeader>

      {shownSections.map(({ section, groups }) => (
        <DetailSectionBlock
          key={section.id}
          component="section"
          aria-labelledby={`hours-detail-${section.id}`}
        >
          <DetailSectionHeading id={`hours-detail-${section.id}`}>
            {resolveHeaderLabel(section.label, labelContext)}
          </DetailSectionHeading>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd(section.groups)}
          >
            <SortableContext
              items={groups.map(({ group }) => group.id)}
              strategy={rectSortingStrategy}
            >
              <DetailGrid>
                {groups.map(({ group, specs }) => (
                  <SortableDetailTable
                    key={group.id}
                    group={group.id}
                    title={resolveHeaderLabel(group.label, labelContext)}
                    rows={specs.map(detailRow)}
                    row={row}
                    showPlusMinus={showPlusMinus}
                    onRemove={() => toggleGroup(group.id)}
                    labels={labels}
                  />
                ))}
              </DetailGrid>
            </SortableContext>
          </DndContext>
        </DetailSectionBlock>
      ))}
    </DetailPanel>
  );
};

export default HoursRowDetailPanel;
